/**
 * Level 3, Lessons 9–13: elapsed time ("count up, hours first"), time conversions, and the four fraction lessons
 * (equal parts, the number line, comparing, equivalent fractions).
 *
 * Fractions follow the trading rule (Meliesa, Oct 29): same-size pieces trade freely long before any strategy uses it,
 * so that the common denominator is the student's own discovery later. Here, "cut every piece the same way" and
 * "trade a half for fourths" come first; the multiply-top-and-bottom rule is something the student notices.
 */
import { int, pick } from "../core";
import type { Item, Skill } from "../types";
import { fr, fracPie, numberLine, partsBar } from "../visuals";

/* ---------- L3.9 Elapsed time ---------- */
const t = (h: number, m: number) => `${h}:${String(m).padStart(2, "0")}`;
const hr12 = (h: number) => ((h - 1 + 120) % 12) + 1;
const after = (h: number, m: number, mins: number) => { const tot = (h % 12) * 60 + m + mins; return { h: hr12(Math.floor(tot / 60) % 12 || 12), m: tot % 60 }; };

export function elapsedItem(h1: number, m1: number, h2: number, m2: number): Item {
  const diff = (((h2 % 12) * 60 + m2 - (h1 % 12) * 60 - m1) % 720 + 720) % 720;
  const H = Math.floor(diff / 60), M = diff % 60;
  const hops: string[] = [];
  for (let i = 0; i <= H; i++) hops.push(t(hr12(h1 + i), m1));
  return {
    id: `elapsed-time:${t(h1, m1)}-${t(h2, m2)}`,
    skill: "elapsed-time",
    prompt: `How much time passes from <b>${t(h1, m1)}</b> to <b>${t(h2, m2)}</b>?`,
    input: { kind: "fields", fields: [{ suffix: "hours", width: 2 }, { suffix: "minutes", width: 2 }] },
    answer: `${H}|${M}`,
    diagnose: (raw) => {
      const [a, b] = raw.split("|").map(Number);
      if (b !== undefined && b >= 60) return { code: "time-base-100", hint: "An hour is 60 minutes, not 100. Count up by hours first, keeping the minutes still." };
      const digitH = ((h2 - h1) % 12 + 12) % 12, digitM = Math.abs(m2 - m1);
      if (a === digitH && b === digitM && (digitH !== H || digitM !== M)) return { code: "elapsed-subtracted-digits", hint: `Count up instead of taking away. Hours first, keeping the minutes still: ${hops.slice(0, 2).join(" → ")}… Stop before you pass ${t(h2, m2)}.` };
      if (a === H + 1 && M > 0) return { code: "elapsed-extra-hour", hint: `One more hour would go past ${t(h2, m2)}. Stop at ${hops[H]}, then count the minutes.` };
      return null;
    },
    hints: [
      `Count up by HOURS first, keeping the minutes still: ${hops.slice(0, Math.min(3, hops.length)).join(" → ")}${H > 2 ? "…" : ""}. Stop before you pass ${t(h2, m2)}.`,
      `${H} hour${H === 1 ? "" : "s"} gets you to ${hops[H]}. Now count the minutes from ${hops[H]} up to ${t(h2, m2)}${m2 < m1 ? " — up to the hour first, then the rest" : ""}.`,
      `${hops.join(" → ")} is ${H} hour${H === 1 ? "" : "s"}; then ${hops[H]} to ${t(h2, m2)} is ${M} minutes.`,
    ],
  };
}
export function endTimeItem(h: number, m: number, dh: number, dm: number): Item {
  const e = after(h, m, dh * 60 + dm);
  const mid = after(h, m, dh * 60);
  const what = `${dh ? `${dh} hour${dh > 1 ? "s" : ""}` : ""}${dh && dm ? " " : ""}${dm ? `${dm} minutes` : ""}`;
  return {
    id: `end-time:${t(h, m)}+${dh}:${dm}`,
    skill: "end-time",
    prompt: `It starts at <b>${t(h, m)}</b> and lasts <b>${what}</b>. When does it end?`,
    input: { kind: "fields", fields: [{ width: 2 }, { label: ":", width: 2 }] },
    answer: `${e.h}|${String(e.m).padStart(2, "0")}`,
    alsoAccept: [`${e.h}|${e.m}`],
    diagnose: (raw) => {
      const [a, b] = raw.split("|").map(Number);
      if (b !== undefined && b >= 60) return { code: "time-base-100", hint: "An hour is 60 minutes. When the minutes reach 60, that is the next hour." };
      if (m + dm >= 60 && a === mid.h && b === e.m) return { code: "elapsed-lost-hour", hint: "The minutes went past the hour — that makes one more hour." };
      return null;
    },
    hints: [
      dh ? `Hours first: ${t(h, m)} plus ${dh} hour${dh > 1 ? "s" : ""}, keeping the minutes still.` : `Count the minutes up from ${t(h, m)}.`,
      m + dm >= 60 ? `From ${t(mid.h, m)}, ${60 - m} minutes gets you to the hour. Then the other ${dm - (60 - m)}.` : `From ${t(mid.h, m)}, count ${dm} more minutes.`,
      `${t(h, m)} → ${t(mid.h, m)} is the hours; ${dm} more minutes lands on ${t(e.h, e.m)}.`,
    ],
  };
}
export const elapsed: Skill = {
  id: "elapsed-time",
  name: "Elapsed time — count up, hours first",
  gen: (rng, level) => {
    const h1 = int(rng, 1, 10), m1 = pick(rng, level < 1 ? [0, 5, 10, 15, 20] : [5, 10, 15, 20, 30, 35, 40, 45, 50]);
    const H = int(rng, level < 1 ? 1 : 0, 4);
    let m2 = level < 1 ? m1 + pick(rng, [5, 10, 15, 20, 25]) : pick(rng, [0, 5, 10, 15, 20, 25, 30, 40, 55]);
    if (m2 > 59) m2 = 55;
    let h2 = hr12(h1 + H);
    if (H === 0 && m2 <= m1) h2 = hr12(h1 + 1);
    return elapsedItem(h1, m1, h2, m2);
  },
};
export const endTime: Skill = {
  id: "end-time",
  name: "When does it end? — start time plus how long",
  gen: (rng, level) => {
    const h = int(rng, 1, 10), m = pick(rng, [5, 10, 15, 20, 30, 35, 40, 45, 50]);
    const dh = int(rng, 0, 2), dm = level < 1 ? pick(rng, [5, 10, 15, 20, 25]) : pick(rng, [10, 15, 20, 25, 30, 40, 45]);
    return endTimeItem(h, m, dh || (dm < 30 ? 1 : 0), dm);
  },
};

/* ---------- L3.9 Time conversions: "the sixes earn their keep" ---------- */
type Conv = "min-s" | "mins-s" | "s-min" | "h-min" | "hmin-min" | "min-h";
export function convertItem(kind: Conv, a: number, b = 0): Item {
  const big = kind.startsWith("h") || kind === "min-h" ? ["hour", "minutes"] : ["minute", "seconds"];
  let prompt = "", ans = 0;
  const hints: string[] = [];
  if (kind === "min-s" || kind === "h-min") { prompt = `${a} ${big[0]}s = ? ${big[1]}`; ans = a * 60; hints.push(`One ${big[0]} is 60 ${big[1]}.`, `${a} sixties: count the sixes in tens — 6, 12, 18… then say it in tens.`, `${a} × 60 = ${ans}.`); }
  if (kind === "mins-s" || kind === "hmin-min") { prompt = `${a} ${big[0]}${a > 1 ? "s" : ""} ${b} ${big[1]} = ? ${big[1]}`; ans = a * 60 + b; hints.push(`Change the ${big[0]}${a > 1 ? "s" : ""} first: one ${big[0]} is 60 ${big[1]}.`, `${a * 60} ${big[1]}, and then the ${b} extra.`, `${a * 60} + ${b} = ${ans}.`); }
  if (kind === "s-min" || kind === "min-h") { prompt = `${a * 60} ${big[1]} = ? ${big[0]}s`; ans = a; hints.push(`How many 60s make ${a * 60}?`, `Count by sixties: 60, 120, 180… — the answer is how many times you counted.`, `${a * 60} ÷ 60 = ${a}.`); }
  return {
    id: `time-convert:${kind}:${a}:${b}`,
    skill: "time-convert",
    prompt: `Convert: <b>${prompt}</b>`,
    input: { kind: "number" },
    answer: String(ans),
    diagnose: (raw) => {
      const n = Number(raw.trim());
      if ((kind === "mins-s" || kind === "hmin-min") && n === a * 100 + b) return { code: "time-base-100", hint: `One ${big[0]} is 60 ${big[1]}, not 100.` };
      if ((kind === "mins-s" || kind === "hmin-min") && n === a * 60) return { code: "time-dropped-extra", hint: `That's the ${big[0]}${a > 1 ? "s" : ""}. Now add the ${b} extra ${big[1]}.` };
      if ((kind === "min-s" || kind === "h-min") && n === a * 100) return { code: "time-base-100", hint: `One ${big[0]} is 60 ${big[1]}, not 100.` };
      return null;
    },
    hints,
  };
}
export const timeConvert: Skill = {
  id: "time-convert",
  name: "Convert minutes and seconds, hours and minutes",
  gen: (rng, level) => {
    const kinds: Conv[] = level < 1 ? ["min-s", "h-min", "s-min", "min-h"] : ["min-s", "mins-s", "s-min", "h-min", "hmin-min", "min-h"];
    const k = pick(rng, kinds);
    return convertItem(k, int(rng, 1, 5), pick(rng, [10, 15, 20, 30, 45]));
  },
};

/* ---------- L3.10 Fractions — equal parts ---------- */
/** Level 3 fractions use only halves, thirds, fourths, sixths and eighths (3.NF; Meliesa, Oct 30). Level 4 has its own
 *  variants (skills/level4-extra.ts) with fifths, tenths, twelfths and hundredths. */
export const L3_DENS = [2, 3, 4, 6, 8] as const;
export function fracReadItem(parts: number, shaded: number, shape: "bar" | "pie" = "bar"): Item {
  return {
    id: `frac-read:${shape}:${shaded}/${parts}`,
    skill: "frac-read",
    prompt: "What fraction is shaded?",
    visual: shape === "pie" ? fracPie(parts, shaded) : partsBar(parts, shaded),
    input: { kind: "fraction" },
    answer: `${shaded}/${parts}`,
    diagnose: (raw) => {
      if (shaded !== parts && raw === `${parts}/${shaded}`) return { code: "frac-flipped", hint: "The bottom number counts the equal parts the whole is cut into. The top counts the ones you have." };
      if (parts - shaded !== shaded && raw === `${parts - shaded}/${parts}`) return { code: "frac-counted-unshaded", hint: "Those are the parts that are NOT shaded. Count the shaded ones." };
      if (parts - shaded !== parts && raw === `${shaded}/${parts - shaded}`) return { code: "frac-part-part", hint: "The bottom counts ALL the equal parts — shaded and not shaded." };
      return null;
    },
    hints: ["How many equal parts is the whole cut into? That's the bottom number.", "How many parts are shaded? That's the top number.", `${parts} equal parts, ${shaded} shaded: ${shaded} over ${parts}.`],
  };
}
export const fracRead: Skill = {
  id: "frac-read",
  name: "Write the fraction a picture shows",
  gen: (rng, level) => { const p = pick(rng, L3_DENS); return fracReadItem(p, int(rng, 1, level < 1 ? p - 1 : p), rng() < 0.5 ? "pie" : "bar"); },
};
export function fracShadeItem(parts: number, top: number, shape: "bar" | "pie" = "bar"): Item {
  return {
    id: `frac-shade:${shape}:${top}/${parts}`,
    skill: "frac-shade",
    prompt: `Shade ${fr(top, parts)}. Tap a part to shade it.`,
    speak: `Shade ${top} ${parts === 2 ? "half" : "out of " + parts}.`,
    input: { kind: "shade", parts, shape },
    answer: String(top),
    diagnose: (raw) => (parts - top !== top && raw.trim() === String(parts - top) ? { code: "frac-counted-unshaded", hint: "The top number tells how many parts to shade." } : null),
    hints: [`The whole is already cut into ${parts} equal parts — the bottom number. The top number says how many to shade.`, "Tap parts until the count under the picture matches the top number.", `${top} of the ${parts} parts shaded.`],
  };
}
export const fracShade: Skill = {
  id: "frac-shade",
  name: "Shade a fraction",
  gen: (rng) => { const p = pick(rng, L3_DENS); return fracShadeItem(p, int(rng, 1, p), rng() < 0.5 ? "pie" : "bar"); },
};

/* ---------- L3.11 The number line: count the hops, not the tick marks ---------- */
export function lineReadItem(parts: number, dot: number): Item {
  return {
    id: `frac-line-read:${dot}/${parts}`,
    skill: "frac-line-read",
    prompt: "What fraction is at the dot?",
    visual: numberLine(parts, dot, ""),
    input: { kind: "fraction" },
    answer: `${dot}/${parts}`,
    diagnose: (raw) => {
      if (raw === `${dot + 1}/${parts + 1}` || raw === `${dot}/${parts + 1}` || raw === `${dot + 1}/${parts}`) return { code: "line-counted-ticks", hint: "Count the hops, not the tick marks. The tick at 0 is where you stand before you hop." };
      if (dot !== parts && raw === `${parts}/${dot}`) return { code: "frac-flipped", hint: "The bottom counts the hops from 0 to 1. The top counts the hops you took." };
      return null;
    },
    hints: ["How many equal hops from 0 to 1? That's the bottom number.", "Start at 0 and count the hops to the dot.", `${parts} hops make the whole; the dot is at the end of hop ${dot}: ${dot} over ${parts}.`],
  };
}
export const lineRead: Skill = {
  id: "frac-line-read",
  name: "Read a fraction on the number line",
  gen: (rng, level) => { const p = pick(rng, L3_DENS); return lineReadItem(p, int(rng, 1, level < 1 ? p - 1 : p)); },
};
export function linePlotItem(parts: number, top: number): Item {
  return {
    id: `frac-line-plot:${top}/${parts}`,
    skill: "frac-line-plot",
    prompt: `Put the dot at ${fr(top, parts)}.`,
    speak: `Put the dot at ${top} out of ${parts}.`,
    input: { kind: "plot", parts },
    answer: String(top),
    diagnose: (raw) => (top >= 1 && raw.trim() === String(top - 1) ? { code: "line-counted-ticks", hint: "Did you count the tick at 0? That's where you stand before you hop. The first hop lands on the next tick." } : null),
    hints: [`The line is cut into ${parts} equal hops. Start at 0.`, `Take ${top} hop${top > 1 ? "s" : ""}.`, `The end of hop ${top}.`],
  };
}
export const linePlot: Skill = {
  id: "frac-line-plot",
  name: "Plot a fraction on the number line",
  gen: (rng, level) => { const p = pick(rng, L3_DENS); return linePlotItem(p, int(rng, 1, level < 1 ? p - 1 : p)); },
};

/* ---------- L3.12 Comparing: same-size pieces → more pieces wins; same count → fewer cuts, bigger pieces ---------- */
export function compareFracItem(a: number, b: number, c: number, d: number): Item {
  const L = a / b, R = c / d;
  const ans = Math.abs(L - R) < 1e-9 ? "=" : L > R ? ">" : "<";
  const flip = ans === ">" ? "<" : ans === "<" ? ">" : "";
  return {
    id: `frac-compare:${a}/${b}:${c}/${d}`,
    skill: "frac-compare",
    prompt: `Compare: ${fr(a, b)} &nbsp;?&nbsp; ${fr(c, d)}`,
    speak: `Compare ${a} over ${b} and ${c} over ${d}.`,
    visual: L <= 1 && R <= 1 ? `<div class="sbs"><figure>${partsBar(b, a, b > 6 ? 26 : 34)}</figure><figure>${partsBar(d, c, d > 6 ? 26 : 34)}</figure></div>` : undefined,
    input: { kind: "choice", options: ["<", ">", "="] },
    answer: ans,
    diagnose: (raw) => {
      if (!flip || raw !== flip) return null;
      if (b !== d && a === c) return { code: "frac-bigger-bottom", hint: "Look at the pieces themselves. A whole cut into MORE pieces has SMALLER pieces." };
      return { code: "compare-flipped", hint: "Same-size pieces: more pieces wins. The open mouth eats the bigger one." };
    },
    hints: b === d
      ? ["Same bottom number — the pieces are the same size.", "Same-size pieces: more pieces wins.", `${a} ${a > c ? "is more than" : a < c ? "is less than" : "is the same as"} ${c} pieces of the same size: ${ans}.`]
      : ["Same top number — the same count of pieces. Which pieces are bigger?", "The fewer pieces a whole is cut into, the bigger each piece. Smaller bottom number wins.", `${b < d ? `${fr(a, b)} has the bigger pieces` : `${fr(c, d)} has the bigger pieces`}: ${ans}.`],
  };
}
export const fracCompare: Skill = {
  id: "frac-compare",
  name: "Compare fractions — same bottom or same top",
  gen: (rng, level) => {
    if (level < 1 || rng() < 0.5) {
      const b = pick(rng, [3, 4, 6, 8]), hi = level >= 2 ? b + 4 : b;
      const a = int(rng, 1, hi);
      let c = int(rng, 1, hi);
      if (c === a && rng() < 0.75) c = a === 1 ? 2 : a - 1;
      return compareFracItem(a, b, c, b);
    }
    const [b, d] = [pick(rng, [2, 3, 4, 6]), pick(rng, [3, 4, 6, 8])];
    const a = int(rng, 1, Math.min(b, d));
    return compareFracItem(a, b, a, d === b ? 8 : d);
  },
};

/* ---------- L3.13 Equivalent fractions: same amount, different cut ---------- */
export function equivItem(a: number, b: number, k: number, missing: "top" | "bottom" = "top"): Item {
  const top = missing === "top";
  const ans = top ? a * k : b * k;
  const known = top ? b * k : a * k;
  return {
    id: `frac-equivalent:${a}/${b}x${k}:${missing}`,
    skill: "frac-equivalent",
    prompt: `Same amount, different cut: ${fr(a, b)} = ${top ? fr("?", b * k) : fr(a * k, "?")}`,
    speak: `${a} over ${b} equals ${top ? `what over ${b * k}` : `${a * k} over what`}?`,
    input: { kind: "number" },
    answer: String(ans),
    diagnose: (raw) => {
      const n = Number(raw.trim());
      const added = top ? a + (b * k - b) : b + (a * k - a);
      if (added !== ans && n === added) return { code: "equiv-added", hint: `Cutting every piece doesn't add the same number of pieces — it multiplies them. What was the ${top ? "bottom" : "top"} multiplied by?` };
      if (top && k > 1 && n === a) return { code: "equiv-kept-top", hint: "The pieces got smaller, so you need more of them to cover the same amount." };
      return null;
    },
    hints: [
      `What happened to the ${top ? "bottom" : "top"}? ${top ? b : a} became ${known}.`,
      `Every piece was cut into ${k} pieces, so the ${top ? "top" : "bottom"} gets multiplied by ${k} as well.`,
      `${top ? a : b} × ${k} = ${ans}: ${fr(a, b)} = ${top ? fr(ans, b * k) : fr(a * k, ans)}.`,
    ],
  };
}
const L3_EQUIV: [number, number, number][] = [[1, 2, 2], [1, 2, 3], [1, 2, 4], [1, 3, 2], [2, 3, 2], [1, 4, 2], [3, 4, 2], [2, 4, 2]];
export const fracEquivalent: Skill = {
  id: "frac-equivalent",
  name: "Find the missing number in an equivalent fraction",
  gen: (rng, level) => {
    // Level 3: every result stays in halves, thirds, fourths, sixths, eighths (1/2 → 2/4, 3/6, 4/8; 1/3 → 2/6; 3/4 → 6/8).
    const [a, b, k] = pick(rng, L3_EQUIV);
    return equivItem(a, b, k, level >= 1 && rng() < 0.4 ? "bottom" : "top");
  },
};

export const LEVEL3_L9TO13_SKILLS: Skill[] = [elapsed, endTime, timeConvert, fracRead, fracShade, lineRead, linePlot, fracCompare, fracEquivalent];

/* ---------- Spiral review: every table, × and ÷ mixed (Lessons 9–20 warm-ups) ---------- */
export function mixedFactItem(a: number, b: number, div: boolean): Item {
  const p = a * b;
  const anchor = b <= 7 ? 5 : 10;
  return div
    ? {
        id: `facts-mixed:${p}/${a}`, skill: "facts-mixed", prompt: `${p} ÷ ${a} = ?`, input: { kind: "number" }, answer: String(b),
        diagnose: (raw) => (raw.trim() === String(p) ? { code: "div-landed-not-count", hint: "That's the number you landed on. The answer is how many times you counted." } : null),
        hints: [`How many ${a}s make ${p}?`, `Jump to the nearest anchor: the ${anchor}th ${a} is ${a * anchor}. Count the short way from there.`, `${a} × ${b} = ${p}, so ${p} ÷ ${a} = ${b}.`],
      }
    : {
        id: `facts-mixed:${a}x${b}`, skill: "facts-mixed", prompt: `${a} × ${b} = ?`, input: { kind: "number" }, answer: String(p),
        hints: [`Count by ${a}s, or turn it around and count by ${b}s.`, `Jump to the nearest anchor: ${a} × ${anchor} = ${a * anchor}, then count the short way.`, `${a} × ${b} = ${p}.`],
      };
}
export const factsMixed: Skill = {
  id: "facts-mixed",
  name: "Every table, times and divide, mixed",
  gen: (rng) => mixedFactItem(pick(rng, [3, 4, 6, 7, 8, 9, 11, 12]), int(rng, 2, 12), rng() < 0.5),
};
LEVEL3_L9TO13_SKILLS.push(factsMixed);
